import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Comment } from './comment.js';

export enum UserType {
  USER = 'USER',
  BOT = 'BOT',
  ADMIN = 'ADMIN',
  LEAD = 'LEAD',
}

registerEnum({ enum: UserType, name: 'UserType' });

export enum MessageType {
  COMMENT = 'COMMENT',
  NOTE = 'NOTE',
}

registerEnum({ enum: MessageType, name: 'MessageType' });

@Entity()
export class IntercomConversationItemEntity extends AdornisEntity {
  static override _class = 'IntercomConversationItemEntity';
  @Field(() => ID) _id!: string;

  @Field(() => String) conversationItemId: Maybe<string>;
  @Field(() => String) author: Maybe<string>;
  @Field(() => String) authorId: Maybe<string>;
  @Field(() => String) body: Maybe<string>;
  @Field(() => String) linkedProduct: Maybe<string>;
  @Field(() => UserType) userType: Maybe<UserType>;
  @Field(() => MessageType) messageType: Maybe<MessageType>;
  @Field(() => [String]) odooTags: Maybe<string[]>;
  @Field(() => Float) rating: Maybe<number>; // only relevant for user type bot
  @Field(() => String) ratingText: Maybe<string>; // only relevant for user type bot
  @Field(() => Date) createdAt: Maybe<Date>;
}

@Entity()
export class IntercomEntity extends MongoEntity {
  static override _class = 'IntercomEntity';
  static override _collectionName = 'intercom';

  @Field(() => String) intercomId: Maybe<string>;
  @Field(() => Boolean) isAgentAssist: Maybe<boolean>;
  @Field(type => [IntercomConversationItemEntity]) intercomConversationItems: Maybe<IntercomConversationItemEntity[]>;

  @Field(() => String) lastAuthorType: Maybe<string>;
  @Field(() => String) message: Maybe<string>;
  @Field(() => String) botResponse: Maybe<string>;
  @Field(() => String) comment: Maybe<string>;
  @Field(() => [Comment]) comments: Maybe<Comment[]>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => String) stringifiedSourceObj: Maybe<string>;
  @Field(() => String) productIdentification: Maybe<string>;
  @Field(() => Float) responseTime: Maybe<number>;
  @Field(() => Boolean) isHistoricalOrIntegrationTest: Maybe<boolean>;
  @Field(() => Boolean) integrationTestKey: Maybe<boolean>;
  @Field(() => Boolean) isCheckedBySupport: Maybe<boolean>;

  static override async beforeCreate(entity: IntercomEntity) {
    await super.beforeCreate(entity);
    entity.createdAt = new Date();
    entity.isAgentAssist = true;
    entity.intercomConversationItems = [];
  }
}

export async function getIntercomConversationById(conversationId: string): Promise<IntercomEntity | null> {
  const collection = await getCollection<IntercomEntity>(IntercomEntity._class);
  const intercomObj = await collection.findOne({ intercomId: conversationId });
  return intercomObj ? new IntercomEntity(intercomObj) : null;
}

export async function appendIntercomConversationItem(
  conversationId: string,
  item: IntercomConversationItemEntity,
): Promise<IntercomEntity | null> {
  const collection = await getCollection<IntercomEntity>(IntercomEntity._class);
  const plainItem = {
    _id: A.getGloballyUniqueID(),
    conversationItemId: item.conversationItemId,
    author: item.author ?? null,
    authorId: item.authorId ?? null,
    userType: item.userType ?? null,
    body: item.body ?? '',
  };

  const intercomObj = await collection.findOneAndUpdate(
    { intercomId: conversationId },
    { $push: { intercomConversationItems: plainItem as unknown as IntercomConversationItemEntity } },
    { returnDocument: 'after' },
  );
  return intercomObj ? new IntercomEntity(intercomObj) : null;
}

export async function storeLlmResponse(conversationId: string, responseObj: { response: string; tagsArray: string[] }) {
  const responseText = responseObj.response;
  const tagsArray = responseObj.tagsArray || [];
  const intercomEntity = await getIntercomConversationById(conversationId);
  if (!intercomEntity) {
    logger.error(`Conversation with ID ${conversationId} not found.`);
    return false;
  }

  const children = intercomEntity.intercomConversationItems || [];
  const lastChildId = children.length > 0 ? children[children.length - 1].conversationItemId : null;
  await appendIntercomConversationItem(
    conversationId,
    new IntercomConversationItemEntity({
      messageType: intercomEntity.isAgentAssist ? MessageType.NOTE : MessageType.COMMENT,
      conversationItemId: lastChildId ? `${lastChildId}-response` : `response-${new Date().getTime()}`,
      authorId: 'bot',
      author: 'NDI-Bot',
      userType: UserType.BOT,
      body: responseText,
      odooTags: tagsArray,
    }),
  );
  return true;
}
